import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

/**
 * Public widget renderer. Loaded as iframe from an external site.
 * Path: /widget/:id
 * Uses only inline styles — no Tailwind, no React context.
 */
export default function WidgetView() {
    const { id } = useParams();
    const [w, setW] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        const url = `${(process.env.REACT_APP_BACKEND_URL || '')}/api/widgets/${id}`;
        axios.get(url).then(r => setW(r.data)).catch(e => setErr(e?.response?.data?.detail || 'Error'));
    }, [id]);

    if (err) return <div style={{ padding: 16, fontSize: 13, color: '#dc2626', fontFamily: 'system-ui,sans-serif' }}>Widget no disponible: {err}</div>;
    if (!w)  return <div style={{ padding: 16, fontSize: 12, color: '#9ca3af', fontFamily: 'system-ui,sans-serif' }}>Cargando widget…</div>;

    const cfg       = w.config || {};
    const isDark    = cfg.theme === 'dark';
    const bg        = isDark ? '#0b0b0b' : '#ffffff';
    const fg        = isDark ? '#f5f5f5' : '#111827';
    const muted     = isDark ? '#9ca3af' : '#6b7280';
    const cardBg    = isDark ? '#1c1c1c' : '#f9fafb';
    const cardBord  = isDark ? '#2d2d2d' : '#e5e7eb';
    const primary   = cfg.primary_color || '#dc2626';
    const radius    = cfg.radius ?? 16;

    const trackClick = () => {
        try { axios.post(`${(process.env.REACT_APP_BACKEND_URL || '')}/api/widgets/${id}/click`); } catch {}
    };

    const marketplaceOrigin = 'https://oklovendo.okgo.mx';

    // Compute these first — used in wrapStyle below
    const isAdWidget = ['user_ads', 'featured_ads', 'ad_carousel'].includes(w.widget_type);
    const isCarousel = cfg.carousel === true || w.widget_type === 'ad_carousel';

    const wrapStyle = {
        background: bg, color: fg,
        padding: 12,
        borderRadius: radius,
        fontFamily: 'ui-sans-serif,system-ui,-apple-system,sans-serif',
        boxSizing: 'border-box',
        /*
         * Use 100vh so the widget fills the iframe viewport regardless of whether
         * the outer html/body/root elements have an explicit height. This is the
         * only reliable anchor for the flex-fill carousel height chain.
         */
        height: '100vh', width: '100%',
        /* In carousel mode the grid fills all vertical space — no y-scroll needed */
        overflow: isCarousel ? 'hidden' : 'hidden auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    };

    const btnStyle = {
        background: primary, color: '#fff',
        padding: '8px 14px', borderRadius: radius / 2,
        display: 'inline-block', textDecoration: 'none',
        fontSize: 13, fontWeight: 600, border: 0,
        cursor: 'pointer', textAlign: 'center',
    };

    // Inline price formatter
    const fmtPrice = (n, cur = 'MXN') =>
        new Intl.NumberFormat('es-MX', { style: 'currency', currency: cur.toUpperCase() }).format(n || 0);

    return (
        <div style={wrapStyle} data-testid="widget-root">

            {/* ── OkLoVendo logo header ─────────────────────────────────── */}
            {cfg.show_logo !== false && isAdWidget && (
                <OkLogoHeader primary={primary} isDark={isDark} marketplaceOrigin={marketplaceOrigin} trackClick={trackClick} />
            )}

            {/* ── Widget title ──────────────────────────────────────────── */}
            {cfg.show_title && (cfg.title || w.data?.nickname) && (
                <div style={{ fontWeight: 700, fontSize: 15, color: fg }}>
                    {cfg.title || (w.data?.nickname ? `@${w.data.nickname}` : '')}
                </div>
            )}

            {/* ── Ad widgets: user_ads · featured_ads · ad_carousel ─────── */}
            {isAdWidget && (
                <AdGrid
                    ads={(w.data?.items || []).slice(0, cfg.items || 6)}
                    carousel={isCarousel}
                    fillHeight={isCarousel}
                    radius={radius}
                    cardBg={cardBg}
                    cardBord={cardBord}
                    fg={fg}
                    muted={muted}
                    primary={primary}
                    marketplaceOrigin={marketplaceOrigin}
                    trackClick={trackClick}
                    fmtPrice={fmtPrice}
                />
            )}

            {/* ── User carousel ─────────────────────────────────────────── */}
            {w.widget_type === 'user_carousel' && (
                <UserCarousel
                    users={(w.data?.users || []).slice(0, cfg.items || 8)}
                    orientation={cfg.carousel_orientation || 'horizontal'}
                    radius={radius}
                    cardBg={cardBg}
                    cardBord={cardBord}
                    fg={fg}
                    muted={muted}
                    primary={primary}
                    marketplaceOrigin={marketplaceOrigin}
                    trackClick={trackClick}
                />
            )}

            {/* ── Search bar ────────────────────────────────────────────── */}
            {w.widget_type === 'search_bar' && (
                <form onSubmit={(e) => {
                    e.preventDefault();
                    trackClick();
                    window.open(`${marketplaceOrigin}/search?q=${encodeURIComponent(e.target.q.value)}`, '_blank');
                }}>
                    <input
                        name="q"
                        placeholder="Buscar en el marketplace…"
                        style={{
                            width: '100%', padding: '10px 14px', boxSizing: 'border-box',
                            borderRadius: radius / 1.5, border: `1px solid ${cardBord}`,
                            background: cardBg, color: fg, fontSize: 14,
                        }}
                    />
                    <button type="submit" style={{ ...btnStyle, marginTop: 8, width: '100%' }}>
                        {cfg.cta_label || 'Buscar'}
                    </button>
                </form>
            )}

            {/* ── Rating summary ────────────────────────────────────────── */}
            {w.widget_type === 'rating_summary' && (
                <div style={{ textAlign: 'center', padding: 8 }}>
                    <div style={{ fontSize: 44, fontWeight: 800, color: primary, lineHeight: 1 }}>
                        {(w.data?.avg || 0).toFixed(1)}
                    </div>
                    <div style={{ color: '#f59e0b', fontSize: 24, marginTop: 4 }}>
                        {'★'.repeat(Math.round(w.data?.avg || 0))}{'☆'.repeat(5 - Math.round(w.data?.avg || 0))}
                    </div>
                    <div style={{ color: muted, fontSize: 12, marginTop: 4 }}>
                        {w.data?.total || 0} calificaciones
                    </div>
                    {w.data?.nickname && (
                        <a href={`${marketplaceOrigin}/u/${w.data.nickname}`} target="_blank" rel="noreferrer"
                            onClick={trackClick} style={{ ...btnStyle, marginTop: 12, display: 'inline-block' }}>
                            Ver perfil
                        </a>
                    )}
                </div>
            )}

            {/* ── Single ad ─────────────────────────────────────────────── */}
            {w.widget_type === 'single_ad' && w.data?.ad && (
                <SingleAdCard
                    ad={w.data.ad}
                    radius={radius}
                    cardBg={cardBg}
                    cardBord={cardBord}
                    fg={fg}
                    primary={primary}
                    marketplaceOrigin={marketplaceOrigin}
                    trackClick={trackClick}
                    fmtPrice={fmtPrice}
                    cfg={cfg}
                    btnStyle={btnStyle}
                />
            )}

            {/* ── User panel shortcut ───────────────────────────────────── */}
            {w.widget_type === 'user_panel_shortcut' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(w.data?.links || []).map((l, i) => (
                        <a key={i} href={`${marketplaceOrigin}${l.url}`} target="_blank" rel="noreferrer"
                            onClick={trackClick}
                            style={{
                                background: cardBg, border: `1px solid ${cardBord}`,
                                padding: '10px 14px', borderRadius: radius / 1.5,
                                textDecoration: 'none', color: fg,
                                display: 'flex', alignItems: 'center', gap: 8,
                                fontWeight: 600, fontSize: 13,
                            }}>
                            <span style={{ color: primary }}>→</span> {l.label}
                        </a>
                    ))}
                </div>
            )}

            {/* ── CTA button ────────────────────────────────────────────── */}
            {cfg.cta_url && !['single_ad', 'search_bar', 'rating_summary'].includes(w.widget_type) && (
                <a href={cfg.cta_url} target="_blank" rel="noreferrer" onClick={trackClick}
                    style={{ ...btnStyle, display: 'block', marginTop: 4 }}>
                    {cfg.cta_label || 'Ver más'}
                </a>
            )}

            {/* ── Powered-by footer ─────────────────────────────────────── */}
            <div style={{ marginTop: 'auto', paddingTop: 8, textAlign: 'right', fontSize: 10, color: muted }}>
                Powered by{' '}
                <a href={marketplaceOrigin} target="_blank" rel="noreferrer"
                    style={{ color: primary, textDecoration: 'none', fontWeight: 600 }}>
                    Ok te lo Vendo
                </a>
            </div>
        </div>
    );
}

// ─── OkLoVendo Logo Header ────────────────────────────────────────────────────

function OkLogoHeader({ primary, isDark, marketplaceOrigin, trackClick }) {
    const fg = isDark ? '#f5f5f5' : '#111827';
    const border = isDark ? '#2d2d2d' : '#f3f4f6';
    return (
        <a href={marketplaceOrigin} target="_blank" rel="noreferrer" onClick={trackClick}
            style={{
                display: 'flex', alignItems: 'center', gap: 8,
                textDecoration: 'none', paddingBottom: 8,
                borderBottom: `1px solid ${border}`,
            }}>
            {/* Inline SVG logo mark */}
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="28" height="28" rx="7" fill={primary} />
                <text x="14" y="20" textAnchor="middle" fill="white"
                    style={{ fontSize: 13, fontWeight: 800, fontFamily: 'ui-sans-serif,system-ui,sans-serif' }}>
                    ok
                </text>
            </svg>
            <div style={{ lineHeight: 1.1 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: fg, letterSpacing: '-0.3px' }}>
                    Ok te lo Vendo
                </div>
                <div style={{ fontSize: 10, color: primary, fontWeight: 600 }}>
                    oklovendo.okgo.mx
                </div>
            </div>
        </a>
    );
}

// ─── Ad Grid / Carousel ───────────────────────────────────────────────────────

function AdGrid({ ads, carousel, fillHeight, radius, cardBg, cardBord, fg, muted, primary, marketplaceOrigin, trackClick, fmtPrice }) {
    if (ads.length === 0) {
        return <div style={{ color: muted, fontSize: 12, textAlign: 'center', padding: '16px 0' }}>Sin anuncios disponibles.</div>;
    }

    const cardRadius = Math.max(8, radius / 1.5);
    const imgBase = process.env.REACT_APP_BACKEND_URL || '';

    const imgSrc = (ad) => {
        const url = ad.media?.[0]?.url;
        if (!url) return null;
        return url.startsWith('http') ? url : `${imgBase}${url}`;
    };

    /**
     * AdCardWidget — two visual modes:
     *   fillHeight=false  → fixed width 150px + 4:5 paddingTop aspect ratio (compact embed)
     *   fillHeight=true   → width auto via aspect-ratio CSS; height fills parent flex container
     */
    const AdCardWidget = ({ ad, fillHeight }) => {
        const imgSection = fillHeight ? (
            /* Flexible height: image grows, info bar stays at bottom */
            <div style={{ flex: 1, minHeight: 0, position: 'relative', background: '#e5e7eb', overflow: 'hidden' }}>
                {imgSrc(ad) ? (
                    <img
                        src={imgSrc(ad)}
                        alt={ad.title}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                        loading="lazy"
                    />
                ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 11 }}>
                        Sin foto
                    </div>
                )}
                {ad.condition && (
                    <div style={{ position: 'absolute', top: 5, left: 5, background: 'rgba(255,255,255,0.92)', color: '#374151', fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 4, textTransform: 'capitalize' }}>
                        {(ad.condition || '').replace('_', ' ')}
                    </div>
                )}
                {ad.discount_percent > 0 && (
                    <div style={{ position: 'absolute', bottom: 5, right: 5, background: '#10b981', color: '#fff', fontSize: 9, fontWeight: 800, padding: '2px 5px', borderRadius: 4 }}>
                        -{Math.round(ad.discount_percent)}%
                    </div>
                )}
            </div>
        ) : (
            /* Fixed aspect ratio: 4:5 via paddingTop */
            <div style={{ position: 'relative', width: '100%', paddingTop: '125%', background: '#e5e7eb', overflow: 'hidden' }}>
                {imgSrc(ad) ? (
                    <img src={imgSrc(ad)} alt={ad.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 11 }}>Sin foto</div>
                )}
                {ad.condition && (
                    <div style={{ position: 'absolute', top: 5, left: 5, background: 'rgba(255,255,255,0.92)', color: '#374151', fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 4, textTransform: 'capitalize' }}>
                        {(ad.condition || '').replace('_', ' ')}
                    </div>
                )}
                {ad.discount_percent > 0 && (
                    <div style={{ position: 'absolute', bottom: 5, right: 5, background: '#10b981', color: '#fff', fontSize: 9, fontWeight: 800, padding: '2px 5px', borderRadius: 4 }}>
                        -{Math.round(ad.discount_percent)}%
                    </div>
                )}
            </div>
        );

        return (
            <a
                href={`${marketplaceOrigin}/ad/${ad.id}`}
                target="_blank"
                rel="noreferrer"
                onClick={trackClick}
                style={{
                    background: cardBg,
                    border: `1px solid ${cardBord}`,
                    borderRadius: cardRadius,
                    overflow: 'hidden',
                    textDecoration: 'none',
                    color: fg,
                    transition: 'box-shadow 0.15s',
                    /* Fill-height mode: flex column so image grows + info bar sticks to bottom */
                    ...(fillHeight ? {
                        display: 'flex', flexDirection: 'column',
                        height: '100%',
                        /* Width driven by aspect-ratio so card is 4:5 regardless of container height */
                        aspectRatio: '4 / 5',
                        flexShrink: 0,
                    } : {
                        display: 'block',
                        flexShrink: 0,
                        width: carousel ? 150 : '100%',
                    }),
                }}
            >
                {imgSection}
                {/* Info bar */}
                <div style={{ padding: '8px 8px 10px', flexShrink: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: primary, lineHeight: 1.1 }}>
                        {fmtPrice(ad.price, ad.currency)}
                    </div>
                    {ad.compare_at_price > ad.price && (
                        <div style={{ fontSize: 10, color: '#9ca3af', textDecoration: 'line-through', marginTop: 1 }}>
                            {fmtPrice(ad.compare_at_price, ad.currency)}
                        </div>
                    )}
                    <div style={{ fontSize: 11, fontWeight: 600, color: fg, marginTop: 3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.3 }}>
                        {ad.title}
                    </div>
                    {ad.location && (
                        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            📍 {ad.location}
                        </div>
                    )}
                </div>
            </a>
        );
    };

    if (carousel) {
        return (
            /*
             * fillHeight=true: the parent (wrapStyle flex-column + 100vh) gives us a
             * definite height via flex:1. We propagate that height down explicitly so
             * every slot and card fills it.
             */
            <div style={{
                display: 'flex',
                gap: 10,
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                alignItems: 'stretch',
                /* When fillHeight: grow to fill the remaining vertical space in the widget */
                ...(fillHeight ? { flex: 1, minHeight: 0 } : {}),
            }}>
                {ads.map(ad => (
                    <div key={ad.id} style={{
                        scrollSnapAlign: 'start',
                        flexShrink: 0,
                        /* In fill-height mode each slot must be 100% of the row height */
                        height: fillHeight ? '100%' : 'auto',
                    }}>
                        <AdCardWidget ad={ad} fillHeight={fillHeight} />
                    </div>
                ))}
            </div>
        );
    }

    // Grid (2 columns)
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8 }}>
            {ads.map(ad => <AdCardWidget key={ad.id} ad={ad} fillHeight={false} />)}
        </div>
    );
}

// ─── Single Ad Card ───────────────────────────────────────────────────────────

function SingleAdCard({ ad, radius, cardBg, cardBord, fg, primary, marketplaceOrigin, trackClick, fmtPrice, cfg, btnStyle }) {
    const imgBase = process.env.REACT_APP_BACKEND_URL || '';
    const img = ad.media?.[0]?.url;
    const imgSrc = img ? (img.startsWith('http') ? img : `${imgBase}${img}`) : null;
    const cardRadius = Math.max(8, radius / 1.5);

    return (
        <a
            href={`${marketplaceOrigin}/ad/${ad.id}`}
            target="_blank" rel="noreferrer"
            onClick={trackClick}
            style={{
                background: cardBg, border: `1px solid ${cardBord}`,
                borderRadius: cardRadius, overflow: 'hidden',
                textDecoration: 'none', color: fg, display: 'block',
            }}
        >
            {/* Image 4:5 ratio */}
            {imgSrc && (
                <div style={{ position: 'relative', width: '100%', paddingTop: '125%', background: '#e5e7eb' }}>
                    <img src={imgSrc} alt={ad.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
            )}
            <div style={{ padding: 12 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: primary }}>{fmtPrice(ad.price, ad.currency)}</div>
                <div style={{ fontSize: 15, fontWeight: 600, marginTop: 4, lineHeight: 1.3 }}>{ad.title}</div>
                {ad.location && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>📍 {ad.location}</div>}
                <div style={{ ...btnStyle, marginTop: 10, display: 'block' }}>{cfg.cta_label || 'Ver anuncio'}</div>
            </div>
        </a>
    );
}

// ─── User Carousel ────────────────────────────────────────────────────────────

function UserCarousel({ users, orientation, radius, cardBg, cardBord, fg, muted, primary, marketplaceOrigin, trackClick }) {
    if (!users || users.length === 0) {
        return <div style={{ color: muted, fontSize: 12, textAlign: 'center', padding: '16px 0' }}>Sin usuarios disponibles.</div>;
    }

    const cardRadius = Math.max(8, radius / 1.5);
    const isHorizontal = orientation !== 'vertical';

    const StarRow = ({ avg }) => {
        const stars = Math.round(avg || 0);
        return (
            <div style={{ color: '#f59e0b', fontSize: 11 }}>
                {'★'.repeat(stars)}{'☆'.repeat(Math.max(0, 5 - stars))}
                <span style={{ color: muted, marginLeft: 4, fontSize: 10 }}>{(avg || 0).toFixed(1)}</span>
            </div>
        );
    };

    const UserCard = ({ user }) => (
        <a
            href={`${marketplaceOrigin}/u/${user.nickname}`}
            target="_blank" rel="noreferrer"
            onClick={trackClick}
            style={{
                flexShrink: 0,
                width: isHorizontal ? 130 : '100%',
                background: cardBg,
                border: `1px solid ${cardBord}`,
                borderRadius: cardRadius,
                overflow: 'hidden',
                textDecoration: 'none',
                color: fg,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '14px 10px 12px',
                gap: 6,
                textAlign: 'center',
                boxSizing: 'border-box',
            }}
        >
            {/* Avatar */}
            <div style={{
                width: isHorizontal ? 52 : 64,
                height: isHorizontal ? 52 : 64,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${primary}33, ${primary}66)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', flexShrink: 0,
                border: `2px solid ${primary}44`,
            }}>
                {user.avatar_url ? (
                    <img
                        src={user.avatar_url.startsWith('http') ? user.avatar_url : `${process.env.REACT_APP_BACKEND_URL || ''}${user.avatar_url}`}
                        alt={user.nickname}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        loading="lazy"
                    />
                ) : (
                    <span style={{ fontSize: isHorizontal ? 22 : 28, lineHeight: 1, color: primary, fontWeight: 800 }}>
                        {(user.nickname || '?')[0].toUpperCase()}
                    </span>
                )}
            </div>

            {/* Name */}
            <div>
                <div style={{
                    fontSize: isHorizontal ? 11 : 13, fontWeight: 700, color: fg,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    maxWidth: isHorizontal ? 110 : '100%',
                }}>
                    @{user.nickname}
                </div>
                {user.full_name && (
                    <div style={{ fontSize: 10, color: muted, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: isHorizontal ? 110 : '100%' }}>
                        {user.full_name}
                    </div>
                )}
            </div>

            {/* Rating */}
            {user.rating_count > 0 && <StarRow avg={user.rating_avg} />}

            {/* Badges */}
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                {user.pro && (
                    <span style={{ background: '#f59e0b', color: '#fff', fontSize: 8, fontWeight: 800, padding: '2px 5px', borderRadius: 4 }}>PRO</span>
                )}
                {user.verified && (
                    <span style={{ background: primary, color: '#fff', fontSize: 8, fontWeight: 800, padding: '2px 5px', borderRadius: 4 }}>✓ VER.</span>
                )}
                {user.ad_count > 0 && (
                    <span style={{ background: cardBord, color: muted, fontSize: 8, fontWeight: 600, padding: '2px 5px', borderRadius: 4 }}>
                        {user.ad_count} anuncios
                    </span>
                )}
            </div>
        </a>
    );

    if (isHorizontal) {
        return (
            <div style={{
                display: 'flex', gap: 10, overflowX: 'auto', scrollSnapType: 'x mandatory',
                paddingBottom: 6, WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none', msOverflowStyle: 'none',
            }}>
                {users.map(u => (
                    <div key={u.id || u.nickname} style={{ scrollSnapAlign: 'start', flexShrink: 0 }}>
                        <UserCard user={u} />
                    </div>
                ))}
            </div>
        );
    }

    // Vertical list
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {users.map(u => (
                <a
                    key={u.id || u.nickname}
                    href={`${marketplaceOrigin}/u/${u.nickname}`}
                    target="_blank" rel="noreferrer"
                    onClick={trackClick}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        background: cardBg, border: `1px solid ${cardBord}`,
                        borderRadius: cardRadius, padding: '10px 12px',
                        textDecoration: 'none', color: fg,
                    }}
                >
                    {/* Avatar */}
                    <div style={{
                        width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                        background: `linear-gradient(135deg, ${primary}33, ${primary}66)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden', border: `2px solid ${primary}44`,
                    }}>
                        {u.avatar_url ? (
                            <img src={u.avatar_url.startsWith('http') ? u.avatar_url : `${process.env.REACT_APP_BACKEND_URL || ''}${u.avatar_url}`}
                                alt={u.nickname} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            <span style={{ fontSize: 18, fontWeight: 800, color: primary }}>{(u.nickname || '?')[0].toUpperCase()}</span>
                        )}
                    </div>
                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            @{u.nickname}
                            {u.pro && <span style={{ marginLeft: 5, background: '#f59e0b', color: '#fff', fontSize: 8, fontWeight: 800, padding: '1px 4px', borderRadius: 3, verticalAlign: 'middle' }}>PRO</span>}
                            {u.verified && <span style={{ marginLeft: 3, background: primary, color: '#fff', fontSize: 8, fontWeight: 800, padding: '1px 4px', borderRadius: 3, verticalAlign: 'middle' }}>✓</span>}
                        </div>
                        {u.full_name && <div style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.full_name}</div>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                            {u.rating_count > 0 && (
                                <span style={{ color: '#f59e0b', fontSize: 11 }}>
                                    {'★'.repeat(Math.round(u.rating_avg || 0))}
                                    <span style={{ color: '#9ca3af', marginLeft: 3, fontSize: 10 }}>{(u.rating_avg || 0).toFixed(1)}</span>
                                </span>
                            )}
                            {u.ad_count > 0 && <span style={{ fontSize: 10, color: '#9ca3af' }}>{u.ad_count} anuncios</span>}
                        </div>
                    </div>
                    <span style={{ color: '#9ca3af', fontSize: 16, flexShrink: 0 }}>›</span>
                </a>
            ))}
        </div>
    );
}
